<template>
  <div class="w-full bg-white flex flex-col gap-2">
    <!-- Event Location -->
    <div>
      <AppInputLabel name="Event location" label="Event location" />
      <div class="flex items-center gap-3">
        <div class="flex-grow mt-2">
          <AddressLookup :show-selected="false" :address="address" placeholder="City or zip code" :is-medium="true"
            @change="addressChange" />
        </div>

        <div class="text-action cursor-pointer" @click.prevent="getLocation">
          <span class="text-lg font-normal">
            <i class="fas fa-location-arrow"></i>
          </span>
        </div>
        <AppSelect :disable-sorting="true" v-model="distance" name="distance" placeholder="Select"
          :options="distanceOptions" :required="false" class="w-32" />
      </div>
    </div>

    <!-- Event Type -->
    <div>
      <AppSelect
        v-model="eventType"
        label="Event type"
        name="eventType"
        placeholder="Select"
        :options="eventTypeOptions"
        :disable-sorting="true"
        :required="false"
        :disable-auto-set="true"
      />
    </div>

    <!-- Event Date Range -->
    <div>
      <AppInputLabel name="Event location" label="Event date range" />
      <div class="flex gap-3">
        <AppInputDate name="dateFrom" placeholder="From" :required="false" minimumView="day" class="flex-1"
          v-model="dateFrom" />
        <AppInputDate name="dateTo" placeholder="To" :required="false" minimumView="day" class="flex-1" v-model="dateTo"
          :right-align="true" />
      </div>
    </div>

    <!-- Exclude Events Toggle -->
    <div>
      <div class="flex items-center justify-between">
        <AppSwitch class="w-full" v-model="excludeHandFee" label="Exclude events with hand fee" />
      </div>
    </div>

    <!-- Done Button -->
    <AppButton title="Done" class="w-full" @click="applyFilters" />
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide } from 'vue';
import { SelectOption } from '@/models/SelectOption';
import { EventFilters } from '@/models/EventFilters';
import AddressLookup from '@/components/AddressLookup.vue';
import { Address } from '@/models/Address';
import SystemDataService from '@/services/SystemDataService';
import { ValidationContext, ValidationContextKey } from '@/models/Context';
import useFormValidation from '@/modules/useFormValidation';

const { errors, validators, registerValidator } = useFormValidation();
// Ensure errors is a Map
const context: ValidationContext = {
  errors,
  registerValidator,
  validators
};

provide(ValidationContextKey, context);

const props = defineProps<{
  defaultFilters: EventFilters;
}>();

const emit = defineEmits<{
  (e: 'filtersApplied', filters: EventFilters): void;
}>();

const coords = ref<{ latitude: number; longitude: number } | null>(null);
const address = ref<Address>({
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  name: '',
  location: null
});
const dateFrom = ref<Date | null>(null);
const dateTo = ref<Date | null>(null);
const excludeHandFee = ref(false);
const distance = ref<number | undefined>(undefined);
const eventType = ref<string | null>(null);
const eventTypeOptions = ref<SelectOption[]>([]);

onBeforeMount(async () => {
  // Initialize with default filters
  dateFrom.value = props.defaultFilters.dateFrom;
  dateTo.value = props.defaultFilters.dateTo;
  excludeHandFee.value = props.defaultFilters.excludeHandFee;
  if (props.defaultFilters.address) {
    address.value = props.defaultFilters.address;
  }
  coords.value = props.defaultFilters.coords ?? null;
  distance.value = props.defaultFilters.distance;
  eventType.value = props.defaultFilters.eventType ?? null;

  // Load event types from system collection
  try {
    const results = await SystemDataService.getEventTypes();
    eventTypeOptions.value = results.map(value => ({
      id: value,
      name: value
    }));
  } catch (error) {
    console.error("Error loading event types:", error);
  }
});

const distanceOptions: SelectOption[] = [
  { id: 10, name: '10 miles' },
  { id: 25, name: '25 miles' },
  { id: 50, name: '50 miles' },
  { id: 100, name: '100 miles' },
];

const addressChange = (newAddress: Address) => {
  address.value = newAddress;
  if (newAddress.location) {
    coords.value = {
      latitude: newAddress.location._lat,
      longitude: newAddress.location._long,
    };
  }
};

const geolocationCallback = (location: { coords: { latitude: number; longitude: number } }) => {
  coords.value = location.coords;
  address.value = {
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    name: 'Current Location',
    location: {
      _lat: location.coords.latitude,
      _long: location.coords.longitude
    }
  };
};

const errorHandler = (error: { code: number }) => {
  if (error.code === 1) {
    console.log('Error: PERMISSION_DENIED: User denied access to their location');
  } else if (error.code === 2) {
    console.log('Error: POSITION_UNAVAILABLE: Network is down.');
  } else if (error.code === 3) {
    console.log('Error: TIMEOUT: Calculating the user\'s location took too long');
  } else {
    console.log('Unexpected error code');
  }
};

const getLocation = () => {
  console.log('Getting location...'); // Add this for debugging
  if (typeof navigator !== 'undefined' && typeof navigator.geolocation !== 'undefined') {
    navigator.geolocation.getCurrentPosition(geolocationCallback, errorHandler);
  } else {
    console.log('Your browser does not support the HTML5 Geolocation API.');
  }
};

const applyFilters = () => {
  emit('filtersApplied', {
    dateFrom: dateFrom.value,
    dateTo: dateTo.value,
    excludeHandFee: excludeHandFee.value,
    address: address.value,
    coords: coords.value ?? undefined,
    distance: distance.value,
    eventType: eventType.value
  });
};
</script>
